import { useEffect, useState } from 'react';
import { Card, CardContent, Typography, Switch, TextField, Button, Stack } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { CustomerAppService } from '../../api/services/customerAppService';
import { setDisplayedProducts } from '../../store/Reducers/CustomerAppSlice';
import { showNotification } from '../../store/Reducers/NotificationSnackbarSlice';
import { IRootState } from '../../types/store';

export const FlashdealScheduleCard = () => {
  const dispatch = useDispatch();

  const { start_time, end_time, enabled } = useSelector((state: IRootState) => state.customerApp);

  const [editMode, setEditMode] = useState(false);
  const [startTime, setStartTime] = useState(start_time || '');
  const [endTime, setEndTime] = useState(end_time || '');

  useEffect(() => {
    setStartTime(start_time || '');
    setEndTime(end_time || '');
  }, [start_time, end_time]);

  const updateEnabled = async (value: boolean) => {
    try {
      const updated = await CustomerAppService.toggleEnableFlashdeal();
      dispatch(setDisplayedProducts(updated));

      dispatch(
        showNotification({
          message: `Flashdeals were successfully ${value ? 'enabled' : 'disabled'}.`,
        }),
      );
    } catch {
      dispatch(
        showNotification({
          message: `Failed to update Flashdeals status.`,
          severity: 'error',
        }),
      );
    }
  };

  const updateSchedule = async (startTime: string, endTime: string) => {
    try {
      const updated = await CustomerAppService.updateFlashdealSchedule({
        start_time: startTime,
        end_time: endTime,
      });

      dispatch(setDisplayedProducts(updated));

      dispatch(
        showNotification({
          message: 'Flashdeal schedule updated.',
        }),
      );

      setEditMode(false);
    } catch {
      dispatch(
        showNotification({
          message: `Failed to update schedule.`,
          severity: 'error',
        }),
      );
    }
  };

  const cancelEdit = () => {
    setStartTime(start_time || '');
    setEndTime(end_time || '');
    setEditMode(false);
  };

  return (
    <Card
      sx={{
        mb: 2,
        borderRadius: 2,
        width: 'fit-content',
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Flashdeal Settings</Typography>

          <Stack direction="row" spacing={1} alignItems="center">
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Enabled</Typography>

            <Switch
              checked={enabled ?? false}
              onChange={(e) => updateEnabled(e.target.checked)}
              size="small"
            />
          </Stack>
        </Stack>

        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1.5, flexWrap: 'wrap' }}>
          <TextField
            label="Start"
            type="time"
            size="small"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            InputLabelProps={{ shrink: true }}
            disabled={!editMode}
            sx={{ width: 130 }}
          />

          <TextField
            label="End"
            type="time"
            size="small"
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
            InputLabelProps={{ shrink: true }}
            disabled={!editMode}
            sx={{ width: 130 }}
          />

          {!editMode && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => setEditMode(true)}
              sx={{ textTransform: 'none', fontSize: 12 }}
            >
              Edit
            </Button>
          )}

          {editMode && (
            <>
              <Button
                variant="contained"
                size="small"
                onClick={() => {
                  updateSchedule(startTime, endTime);
                }}
                sx={{ textTransform: 'none', fontSize: 12 }}
              >
                Save
              </Button>

              <Button
                variant="text"
                size="small"
                onClick={cancelEdit}
                sx={{ textTransform: 'none', fontSize: 12 }}
              >
                Cancel
              </Button>
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};
